// src/components/admin/onboarding/OnboardingManagementV2.js
// Thin tabbed wrapper that preserves the sidebar's ?tab= routing while showing
// the redesigned review queue + checklist config.
//
// Sidebar tabs (admin/AdminSidebar.js): pending | completed | all | configure
//   pending   → review queue, default filter = "ready"
//   completed → review queue, default filter = "approved"
//   all       → review queue, default filter = "all"
//   configure → new checklist configuration UI
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminOnboardingReview from './AdminOnboardingReview';
import AdminChecklistConfig from './AdminChecklistConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { palette as buildPalette } from './onboardingTheme';

const TABS = [
    { id: 'pending',   label: 'Pending review' },
    { id: 'completed', label: 'Approved' },
    { id: 'all',       label: 'All employees' },
    { id: 'configure', label: 'Configure' },
];

const filterForTab = (tab) => {
    if (tab === 'completed') return 'approved';
    if (tab === 'pending') return 'ready';
    return 'all';
};

const OnboardingManagementV2 = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const themeCtx = useTheme();
    const isDark = themeCtx?.isDarkMode ?? (themeCtx?.theme === 'dark');
    const p = useMemo(() => buildPalette(!!isDark), [isDark]);

    const initialTab = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('tab') || 'pending';
    }, [location.search]);

    const [tab, setTab] = useState(initialTab);

    useEffect(() => { setTab(initialTab); }, [initialTab]);

    const onPick = (id) => {
        setTab(id);
        const params = new URLSearchParams(location.search);
        params.set('tab', id);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    return (
        <div style={{ minHeight: 'calc(100vh - 80px)', background: p.appBg }}>
            <div className="tour-onboarding-tabs" style={{ display: 'flex', gap: 6, padding: '14px 28px 0', flexWrap: 'wrap' }}>
                {TABS.map((t) => {
                    const active = t.id === tab;
                    return (
                        <button
                            key={t.id}
                            onClick={() => onPick(t.id)}
                            style={{
                                background: active ? p.chipActiveBg : p.chipBg,
                                color: active ? p.chipActiveFg : p.chipFg,
                                border: `1px solid ${active ? p.chipActiveBd : p.chipBd}`,
                                padding: '7px 14px', borderRadius: 999,
                                fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                                fontFamily: 'inherit',
                            }}
                        >{t.label}</button>
                    );
                })}
            </div>

            {tab === 'configure'
                ? <AdminChecklistConfig />
                : <AdminOnboardingReview key={tab} defaultFilter={filterForTab(tab)} />
            }
        </div>
    );
};

export default OnboardingManagementV2;
